<script module lang="ts">
  /** One line in a LineChart. `values` may contain nulls — the line breaks there. */
  export interface LineSeriesDef {
    name: string;
    values: (number | null)[];
    color?: string;
    dashed?: boolean;
  }
</script>

<script lang="ts">
  // Multi-series line chart (inline SVG, no dependency). Complements AreaChart,
  // which is hardcoded to two filled series on one shared max: this draws N
  // crisp lines and can normalize each series to its own max so measures of
  // different scale share one plot (indexed — never a second axis).
  let {
    series,
    height = 140,
    normalize = false,
    legend = true,
  }: {
    series: LineSeriesDef[];
    height?: number;
    normalize?: boolean;
    legend?: boolean;
  } = $props();

  const W = 100; // viewBox units; SVG scales to container width
  const PALETTE = [
    'var(--color-primary)',
    'var(--color-secondary)',
    'var(--color-info)',
    'var(--color-success)',
    'var(--color-error)',
  ];
  const color = (i: number) => series[i]?.color ?? PALETTE[i % PALETTE.length]!;

  const n = $derived(Math.max(1, ...series.map((s) => s.values.length)));
  const sharedMax = $derived(
    Math.max(1e-6, ...series.flatMap((s) => s.values.filter((v): v is number => v != null))),
  );
  function seriesMax(s: LineSeriesDef): number {
    if (!normalize) return sharedMax;
    return Math.max(1e-6, ...s.values.filter((v): v is number => v != null));
  }

  function x(i: number): number {
    return n <= 1 ? 0 : (i / (n - 1)) * W;
  }
  function y(v: number, max: number): number {
    return height - (v / max) * height;
  }
  function path(s: LineSeriesDef): string {
    const max = seriesMax(s);
    let d = '';
    let pen = false;
    s.values.forEach((v, i) => {
      if (v == null) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(2)},${y(v, max).toFixed(2)} `;
      pen = true;
    });
    return d.trim();
  }
</script>

{#if legend && series.length > 1}
  <div class="mb-2 flex flex-wrap items-center gap-3 font-mono text-[0.7rem] text-base-content/65">
    {#each series as s, i (s.name)}
      <span class="flex items-center gap-1">
        <!-- privacy-ok: a chart SERIES name ("posts", "comments") — names the line, not a person -->
        <span class="h-2 w-2 rounded-sm" style="background:{color(i)}"></span>{s.name}
      </span>
    {/each}
  </div>
{/if}
<svg
  viewBox="0 0 {W} {height}"
  preserveAspectRatio="none"
  style="display:block; width:100%; height:{height}px"
  role="img"
  aria-label="line chart"
>
  {#each series as s, i (s.name)}
    <path
      d={path(s)}
      fill="none"
      stroke={color(i)}
      stroke-width="1"
      stroke-dasharray={s.dashed ? '4 3' : undefined}
      vector-effect="non-scaling-stroke"
      opacity={i === 0 ? 1 : 0.85}
    />
  {/each}
</svg>
